import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ZeosButton } from '../../../components/Shared/ZeosButton';
import { ZeosModal } from '../../../components/Shared/ZeosModal';
import { ZeosSpinner } from '../../../components/Shared/ZeosSpinner';
import { UtilityLinkSelector } from '../../../components/utility-link/UtilityLinkSelector';
import { matchUtilityByName } from '../../../configs/UtilityLinkingConfig';
import { ZeosApiContext } from '../../../context/ZeosApiContext';
import { IPortfolioItem } from '../../../mock-data/portfolioData';
import { IZeosUser } from '../../../services/auth/IZeosUser';
import { createLogger } from '../../../services/Logger';
import { ONBOARDING_BASE_PATH } from '../../../services/nav/Constants';
import { IOnboardStatus } from '../../../services/nav/Interfaces';
import { UapiStatus } from '../../../services/zeos-api/enums/Uapi';
import { OnboardContainer } from '../layout/OnboardContainer';

interface IProps {}

const log = createLogger("UtilityLinkScreen");

export const UtilityLinkScreen = (props: IProps) => {
  const navigate = useNavigate();
  const api = useContext(ZeosApiContext);

  // const handleDummyReferralCode = () => {
  //   // Simulate referral code returned from UtilityAPI
  //   const referralCode = "REF-CODE-123456";
  //   navigate(`/onboarding/uapi-confirm?referralCode=${referralCode}`);
  // };

  const [showSkipButton, setShowSkipButton] = useState<boolean>(true);
  const [showSkipModal, setShowSkipModal] = useState<boolean>(false);
  const [zeosUser, setZeosUser] = useState<IZeosUser>();
  const [isBackPage, setIsBackPage] = useState<boolean>(false);
  const [linkStatus, setLinkStatus] = useState<UapiStatus>(UapiStatus.PENDING);
  const [utilityCode, setUtilityCode] = useState<string | undefined>();
  const [isUtilityResolved, setIsUtilityResolved] = useState(false);
  // const [isContinueDisabled, setIsContinueDisabled] = useState<boolean>(true);

  // Window listener for "uapi-skip" event
  useEffect(() => {
    const handleSkipEvent = () => setShowSkipModal(true);
    window.addEventListener("uapi-skip", handleSkipEvent);
    return () => window.removeEventListener("uapi-skip", handleSkipEvent);
  }, []);

  const handleUserCallback = (user: IZeosUser): void => {
    setZeosUser(user);
  };

  const handleStatusCallback = (status: IOnboardStatus): void => {
    setIsBackPage(status.isBack);
  };

  const handleStatusChange = async (status: UapiStatus) => {
    setLinkStatus(status);
    // log.info("Link Status:", status);
    const linked = status == UapiStatus.LINKED;
    // setIsContinueDisabled(!linked);
    setShowSkipButton(!linked);
    handleStatusCallback({
      isBack: isBackPage,
    });
  };

  const handleSkip = async () => {
    navigate(`${ONBOARDING_BASE_PATH}/uapi-manual`);
  };

  const handleNext = async (doNavigate: () => void) => {
    doNavigate();
  };

  // Create minimal property object from user data
  const property: IPortfolioItem | null = useMemo(() => {
    if (!zeosUser?.customerClaims[0]?.companies[0]?.properties[0]) {
      return null;
    }
    const company = zeosUser.customerClaims[0].companies[0];
    const propertyData = company.properties[0];
    return {
      propertyId: propertyData.propertyId ?? 0,
      companyId: company.companyId ?? 0,
      companyName: company.companyName ?? "",
      name: propertyData.propertyName ?? "",
      image: null,
    };
  }, [zeosUser]);

  // Carry the utility confirmed on the Property step forward (ONB-005).
  // Workaround for ONB-004's data gap: match by name against the
  // UtilityLinkingConfig dictionary; no match just leaves the field empty.
  //
  // UtilityLinkSelector pre-fills via an *uncontrolled* defaultSelected,
  // which react-bootstrap-typeahead only reads at mount time -- so
  // UtilityLinkSelector must not mount until utilityCode is already resolved,
  // otherwise the first (empty) mount wins and a later prop change is ignored.
  useEffect(() => {
    if (!property?.propertyId) {
      return;
    }
    let cancelled = false;
    const loadConfirmedUtility = async () => {
      const response = await api.Property.getProperty(property.propertyId);
      const match =
        matchUtilityByName(response.data?.electricUtilityProvider) ??
        matchUtilityByName(response.data?.gasUtilityProvider);
      if (!cancelled) {
        setUtilityCode(match?.utilityCode);
        setIsUtilityResolved(true);
      }
    };
    loadConfirmedUtility();
    return () => {
      cancelled = true;
    };
  }, [property?.propertyId]);

  const isWaitingOnUtilityMatch = Boolean(property?.propertyId) && !isUtilityResolved;

  const content = (
    <div className="flex flex-col gap-4 font-sans">
      {isWaitingOnUtilityMatch ? (
        <ZeosSpinner />
      ) : (
        <UtilityLinkSelector
          property={property}
          zeosUser={zeosUser}
          // utilityLinkType={undefined}
          utilityCode={utilityCode}
          onStatusChange={handleStatusChange}
          onClose={() => {}}
          showBayouButtons={false}
        />
      )}

      {/* Skip Modal */}
      <ZeosModal isOpen={showSkipModal} onClose={() => setShowSkipModal(false)}>
        <div className="text-center px-4 py-6 sm:px-10 sm:py-8">
          <h2 className="text-2xl font-semi-bold text-[#f58220] mb-4">
            Maximize your savings potential
          </h2>

          <p className="text-sm text-[#333] mb-4">
            Connecting to your utility account is the beginning of how we
            provide powerful energy, time and cost-saving insights and
            recommendations.
          </p>

          <div className="bg-yellow-100 text-sm text-[#333] px-4 py-3 rounded mb-4">
            Authorize now for real-time data-driven insights or continue with
            manual entry.
          </div>

          <p className="text-sm text-[#333] mb-6">
            You can set this up later in settings.
          </p>

          <div className="flex flex-col items-center gap-1">
            <div className="w-[250px]">
              <ZeosButton
                label="Authorize My Account"
                type="button"
                mode="action"
                onPress={() => setShowSkipModal(false)}
                hackWidth
              />
            </div>
            <button onClick={handleSkip} className="text-[#f58220] text-xs">
              I Understand. Skip for Now
            </button>
          </div>
        </div>
      </ZeosModal>
    </div>
  );

  return (
    <OnboardContainer
      content={content}
      contentType="display"
      pageSlug="uapi"
      onNext={handleNext}
      onSkip={() => setShowSkipModal(true)}
      showSkipButton={showSkipButton}
      // isContinueDisabled={isContinueDisabled}
      userCallback={handleUserCallback}
      statusCallback={handleStatusCallback}
      triggerNext={linkStatus === UapiStatus.LINKED}
    />
  );
};
